/**
 * SettingsDropdown Component - Client Component
 * Uses UI components (DropdownMenu, Button) with custom styling
 */

"use client";

import { Settings, Sun, Moon } from "lucide-react";
import { useSettings } from "@/contexts/settings-context";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Language options with flags and native names
const languageOptions = [
  { code: "ko" as const, flag: "🇰🇷", label: "한국어" },
  { code: "en" as const, flag: "🇺🇸", label: "English" },
  { code: "th" as const, flag: "🇹🇭", label: "ภาษาไทย" },
  { code: "uz" as const, flag: "🇺🇿", label: "Oʻzbekcha" },
  { code: "zh" as const, flag: "🇨🇳", label: "中文" },
  { code: "ja" as const, flag: "🇯🇵", label: "日本語" },
  { code: "vi" as const, flag: "🇻🇳", label: "Tiếng Việt" },
];

export default function SettingsDropdown() {
  const { theme, language, setTheme, setLanguage } = useSettings();

  return (
    <DropdownMenu>
      {/* Settings Trigger Button - Using Button from ui */}
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-lg border border-gray-200 bg-gray-100 p-1.5 hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 sm:h-9 sm:w-9 sm:p-2"
          aria-label="Settings"
        >
          <Settings className="h-4 w-4 text-gray-600 dark:text-gray-300 sm:h-5 sm:w-5" />
        </Button>
      </DropdownMenuTrigger>

      {/* Dropdown Content with custom styling */}
      <DropdownMenuContent
        align="end"
        className="w-60 space-y-3 border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 sm:w-72 sm:space-y-4 sm:p-4"
      >
        {/* Theme Mode Section */}
        <div>
          <h3 className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
            Theme Mode
          </h3>
          <div className="flex space-x-1.5 sm:space-x-2">
            {/* Light Mode Button - Using Button from ui */}
            <Button
              onClick={() => setTheme("light")}
              variant="ghost"
              size="sm"
              className={`flex flex-1 items-center justify-center space-x-1 whitespace-nowrap rounded-lg px-1.5 py-1.5 text-xs transition-colors sm:space-x-1.5 sm:px-2 sm:py-2 sm:text-sm ${
                theme === "light"
                  ? "bg-brand-100 text-brand-700 hover:bg-brand-100 dark:bg-brand-900 dark:text-brand-200 dark:hover:bg-brand-900"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              <Sun className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
              <span>Light Mode</span>
            </Button>

            {/* Dark Mode Button - Using Button from ui */}
            <Button
              onClick={() => setTheme("dark")}
              variant="ghost"
              size="sm"
              className={`flex flex-1 items-center justify-center space-x-1 whitespace-nowrap rounded-lg px-1.5 py-1.5 text-xs transition-colors sm:space-x-1.5 sm:px-2 sm:py-2 sm:text-sm ${
                theme === "dark"
                  ? "bg-brand-100 text-brand-700 hover:bg-brand-100 dark:bg-brand-900 dark:text-brand-200 dark:hover:bg-brand-900"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              <Moon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
              <span>Dark Mode</span>
            </Button>
          </div>
        </div>

        {/* Language Selection Section */}
        <div>
          <h3 className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
            Language
          </h3>
          <div className="space-y-1.5 sm:space-y-2">
            {languageOptions.map((lang) => (
              <Button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                variant="ghost"
                size="sm"
                className={`flex w-full items-center justify-start space-x-2 whitespace-nowrap rounded-lg px-2 py-1.5 text-xs transition-colors sm:px-3 sm:py-2 sm:text-sm ${
                  language === lang.code
                    ? "bg-brand-100 text-brand-700 hover:bg-brand-100 dark:bg-brand-900 dark:text-brand-200 dark:hover:bg-brand-900"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                <span className="shrink-0 text-sm sm:text-base">{lang.flag}</span>
                <span>{lang.label}</span>
              </Button>
            ))}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
